import React from 'react'
import { isBotUserAgent } from '@deal/is-bot'
import Logger from '#src/services/logger'
import Redirect from '#src/components/Redirect'
import { Notification } from '@deal/components'
import { getSellableUrl, isInactiveOrOutOfStockSellable } from '#src/services/sellable'
import { invariant } from '#src/services/errors'
import {
  SellableType,
  ExperienceDetailsFragment,
  ProductDetailsFragment
} from '#src/generated/graphql'
import { withUserAgent, WithUserAgentProps } from '#src/containers/UserAgent'

interface SellableRouterProps {
  urlSellableSlug?: string
  sellable: ExperienceDetailsFragment | ProductDetailsFragment
  requestedType: SellableType
}

const SellableRouter: React.SFC<SellableRouterProps & WithUserAgentProps> = ({
  sellable,
  urlSellableSlug,
  requestedType,
  userAgent,
  children
}) => {
  /**
   * For bot traffic, check the slug and make sure it matches correctly. If it's incorrect,
   * 301 the result back to the fully qualified url. We can't reliably do this unless the
   * user agent is definitively there.
   */
  if (
    userAgent &&
    urlSellableSlug &&
    urlSellableSlug !== sellable.slug &&
    isBotUserAgent(userAgent.raw)
  ) {
    return <Redirect to={getSellableUrl(sellable)} statusCode={301} />
  }

  /**
   * If the sellable is inactive or is not a variant and a variation matrix exists, attempt to
   * redirect to the default variant assuming the default is also active.
   */
  const isInactive = isInactiveOrOutOfStockSellable(sellable)
  if (isInactive && sellable.variationMatrix) {
    const isDefaultInactive = isInactiveOrOutOfStockSellable({
      ...sellable.variationMatrix.defaultVariant
    })

    /**
     * As long as the default variant is active, redirect. Otherwise, we'll just render a page with
     * a 'Item is out of stock' message
     */
    if (!isDefaultInactive) {
      return <Redirect to={getSellableUrl(sellable.variationMatrix.defaultVariant)} />
    }
  }

  if (!sellable.variant && sellable.variationMatrix) {
    /**
     * A self-referencing default variant is 'unsellable'
     */
    if (sellable.variationMatrix.defaultVariant.id === sellable.id) {
      return invariant(`The sellable "${sellable.id}" has a self-referencing default variant`)
    }

    return <Redirect to={getSellableUrl(sellable.variationMatrix.defaultVariant)} />
  }

  /**
   * Make sure the sellable type matches the requested type, this will handle if an
   * experiences sellable is on a products route and vice versa
   */
  if (sellable.type !== requestedType) {
    return <Redirect to={getSellableUrl(sellable)} />
  }

  if (children) {
    return <>{children}</>
  } else {
    Logger.captureException('Sellable router invoked without children')

    return (
      <Notification type="error">
        There was an error loading this product, please try again.
      </Notification>
    )
  }
}

export default withUserAgent(SellableRouter)
